import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ChevronLeft, ArrowRight } from "lucide-react";
import SEOMeta from "@/components/SEOMeta";
import ExitIntent from "@/components/ExitIntent";
import { ARTICLES, CATEGORIES } from "@/data/learnArticles";

function renderMarkdown(text) {
  return text
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-section mt-8 mb-3 text-white border-b border-[var(--border)] pb-2">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-sm font-section mt-5 mb-2 txt-secondary">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono txt-accent'>$1</code>")
    .replace(/```[\w]*\n([\s\S]*?)```/g, "<pre class='bg-[var(--bg-surface)] border border-[var(--border)] p-4 rounded text-xs font-mono txt-secondary overflow-x-auto my-4'>$1</pre>")
    .replace(/^\| (.+) \|$/gm, (match) => {
      const cells = match.slice(2, -2).split(" | ");
      return `<tr>${cells.map(c => `<td class="px-3 py-2 text-xs txt-secondary border-b border-[var(--border)]">${c}</td>`).join("")}</tr>`;
    })
    .replace(/^- (.+)$/gm, '<li class="txt-secondary text-sm ml-4 list-disc mb-1">$1</li>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="txt-accent hover:underline">$1</a>')
    .replace(/\n\n/g, '</p><p class="txt-secondary text-sm leading-relaxed mb-4">');
}

export default function LearnArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) return <Navigate to="/learn" replace />;

  const others = ARTICLES.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 3);
  const html = `<p class="txt-secondary text-sm leading-relaxed mb-4">${renderMarkdown(article.body)}</p>`;

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-white">
      <SEOMeta
        title={article.title}
        description={article.description}
        url={`/learn/${article.slug}`}
        type="article"
        keywords={article.keywords}
      />
      <ExitIntent />

      {/* Nav */}
      <nav className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-section text-lg tracking-widest text-white hover:txt-accent transition-colors">
          ALGOFORGE
        </Link>
        <div className="flex items-center gap-6 text-xs font-section tracking-wider txt-muted">
          <Link to="/learn" className="hover:text-white transition-colors">LEARN</Link>
          <Link to="/blog" className="hover:text-white transition-colors">BLOG</Link>
          <Link to="/login" className="hover:text-white transition-colors">LOGIN</Link>
          <Link to="/register" className="px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-muted)] transition-colors">
            START FREE
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <Link to="/learn" className="flex items-center gap-1.5 txt-muted text-xs font-section tracking-wider hover:text-white transition-colors mb-8">
          <ChevronLeft className="w-3.5 h-3.5" /> Learn
        </Link>

        {/* Header */}
        <div className="mb-2">
          <span className="text-xs font-section tracking-wider txt-muted uppercase">
            {CATEGORIES[article.category] || article.category}
          </span>
        </div>
        <h1 className="font-section text-2xl text-white leading-snug mb-3">{article.title}</h1>
        <div className="flex items-center gap-4 txt-muted text-xs mb-8">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime} min read</span>
          <span>AlgoForge Guides</span>
        </div>

        {/* Body */}
        <div
          className="prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {/* Mid-article CTA (after body) */}
        <div className="my-10 panel p-6 border-[var(--accent)]/20">
          <div className="font-section text-sm text-white mb-1">Try it free on AlgoForge</div>
          <p className="txt-secondary text-xs mb-4">
            ₹10,00,000 virtual capital. Real market prices. No credit card required.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider text-xs transition-colors"
          >
            Start Free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Related articles */}
        {others.length > 0 && (
          <div>
            <div className="overline text-xs mb-4">More in {CATEGORIES[article.category]}</div>
            <div className="space-y-1">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  to={`/learn/${a.slug}`}
                  className="flex items-center justify-between group py-3 px-4 hover:bg-[var(--bg-surface)] border border-transparent hover:border-[var(--border)] transition-all"
                >
                  <span className="text-sm text-white group-hover:txt-accent transition-colors">{a.title}</span>
                  <ChevronLeft className="w-4 h-4 txt-muted rotate-180 group-hover:text-white transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
